import { useCallback, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getAeroexpress, getServicesFallbackURL, shouldRedirectToFallback } from '../../Checkout/store/order/selectors';
import { LoadingState } from '../types';
import { useUpsaleWarning } from '../../Checkout/components/Checkout/Upsale/hooks';
import { OrderDocumentType, useOrderDocumentQuery } from '@websky/graphql';
import { downloadPDF } from '../../utils';
import { getAeroexpressPriceToPay } from '../../Checkout/store/orderPrice/selectors';
import { ModeContext } from '../../context';
import { Mode } from '../../types';
import { getHasSelectedAeroexpress } from '../../Modules/Checkin/store/order/selectors';
import { WxEvents } from '../../DataLayerAnalytics/types';
import { useToggleable } from '../../hooks';
import WebskyAnalyticsApollo from '../../DataLayerAnalytics/WebskyAnalyticsApollo';

export const useAeroexpressTicketsDownloader = (orderId: string) => {
	const { refetch: loadDocument } = useOrderDocumentQuery({ skip: true });

	return async () => {
		const { data } = await loadDocument({
			params: {
				orderId,
				documentType: OrderDocumentType.AeroexpressTicket
			}
		});

		if (data?.OrderDocument) {
			await downloadPDF(data.OrderDocument, 'aeroexpress-tickets.pdf');
		}
	};
};

export const useAeroexpress = (updateServices: (ids: string[]) => Promise<void>, orderId: string) => {
	const { t } = useTranslation('Aeroexpress');

	const { isOpen, open, close } = useToggleable(false);

	const downloadAeroexpress = useAeroexpressTicketsDownloader(orderId);

	const aeroexpress = useSelector(getAeroexpress);
	let hasSelectedServices: boolean;

	const mode = useContext(ModeContext);
	if (mode === Mode.Checkin) {
		hasSelectedServices = useSelector(getHasSelectedAeroexpress);
	} else {
		hasSelectedServices = useSelector(getAeroexpressPriceToPay)?.amount > 0;
	}
	const availableServices = aeroexpress.trips;
	const selectedServices = aeroexpress.selectedTrips ?? [];
	const hasConfirmedServices = selectedServices.some(selectedService =>
		selectedService?.tickets?.some(ticket => !!ticket?.imageUrl)
	);

	const [loadingState, setLoadingState] = useState<LoadingState>(
		availableServices.reduce<LoadingState>((loadingState, service) => {
			loadingState[service.id] = false;

			return loadingState;
		}, {})
	);
	const { showWarning, closeWarning, toggleWarningIfOrderConfirmed } = useUpsaleWarning();
	const shouldRedirect = useSelector(shouldRedirectToFallback);
	const servicesRedirectURL = useSelector(getServicesFallbackURL);

	const onSelect = useCallback(
		async (id: string) => {
			const selectedServicesIds = selectedServices.map(service => service.id);
			setLoadingState(prev => ({ ...prev, ...{ [id]: true } }));

			if (selectedServicesIds.includes(id)) {
				selectedServicesIds.splice(
					selectedServicesIds.findIndex(serviceId => serviceId === id),
					1
				);
				await updateServices(selectedServicesIds);
			} else {
				selectedServicesIds.push(id);
				await updateServices(selectedServicesIds);
			}
			setLoadingState(prev => ({ ...prev, ...{ [id]: false } }));
		},
		[selectedServices]
	);

	const availableServicesCount = availableServices.length;
	let tickets: string[] = [];

	selectedServices.forEach(service => {
		if (service.tickets && service.tickets?.length) {
			tickets = tickets.concat(service?.tickets.map(ticket => ticket.imageUrl));
		}
	});

	useEffect(() => {
		if (isOpen) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.AeroexpressList);
		}
	}, [isOpen]);

	const handleOpenModalClick = async () => {
		if (shouldRedirect) {
			toggleWarningIfOrderConfirmed();

			return;
		}

		if (hasConfirmedServices && availableServicesCount > 0) {
			await downloadAeroexpress();

			return;
		}

		open();
	};

	const price = {
		amount: hasSelectedServices
			? selectedServices.reduce((acc, curr) => +acc + +curr.price.amount, 0)
			: Math.min(...availableServices.map(service => service.price.amount)),
		currency: availableServices[0].price.currency
	};

	const setLoadingStateForAllTo = (value: boolean) => {
		setLoadingState(
			availableServices.reduce<LoadingState>((loadingState, service) => {
				loadingState[service.id] = value;

				return loadingState;
			}, {})
		);
	};

	const onClear = async () => {
		setLoadingStateForAllTo(true);

		await updateServices([]);
		close();

		setLoadingStateForAllTo(false);
	};

	let buttonText = t('Add');

	if (hasConfirmedServices) {
		buttonText = t('Download tickets');
	}

	if (hasSelectedServices && !hasConfirmedServices) {
		buttonText = t(availableServicesCount === 1 ? 'Cancel' : 'Change');
	}

	const isLoading = Object.keys(loadingState).reduce<boolean>((isLoading, key) => {
		return isLoading ? isLoading : loadingState[key];
	}, false);

	const onOpenHandler = () => {
		if (hasConfirmedServices) {
			downloadAeroexpress();
		} else {
			open();
		}
	};

	return {
		buttonText,
		isLoading,
		price,
		onClear,
		handleOpenModalClick,
		hasSelectedServices,
		showWarning,
		servicesRedirectURL,
		closeWarning,
		tickets,
		isOpen,
		open: onOpenHandler,
		close,
		loadingState,
		onSelect,
		handleDownloadTickets: downloadAeroexpress
	};
};
